import { strapiTimeFrag } from "@/lib/helper";
import { basierCirleBold } from "@/styles/fonts";
import theme from "@/styles/theme";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import { grey } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import { useState } from "react";
import { FormattedDate } from "react-intl";
import { CategoryTag } from "../CategoryTag";
import { StrapiImageAsset } from "../StrapiImageAsset";

export const WorkshopEntry = ({ workshop, onExpandChanged, ...props }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = (event) => {
    setExpanded(!expanded);

    if (onExpandChanged) {
      onExpandChanged(!expanded);
    }
  };

  return (
    <>
      <Grid
        key={workshop.id}
        item
        sm={6}
        md={4}
        sx={{
          [theme.breakpoints.down("md")]: {
            display: "none",
          },
          "& img": {
            mixBlendMode: "multiply",
          },
        }}
      >
        <StrapiImageAsset
          strapiImageObject={workshop.logo}
          layout="responsive"
          objectFit="contain"
          objectPosition="left"
          width={160}
          height={90}
          fixedHeight={true}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              component="p"
              sx={{
                fontWeight: basierCirleBold.style.fontWeight,
                fontFamily: basierCirleBold.style.fontFamily,
                textTransform: "uppercase",
                color: theme.palette.common.black,
              }}
            >
              {workshop.title}{" "}
              <CategoryTag
                text={workshop.category.name}
                variant={`category--${workshop.category.name}`}
                color={workshop.category.color}
              />
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              [theme.breakpoints.up("md")]: {
                display: "none",
              },
            }}
          >
            <StrapiImageAsset
              strapiImageObject={workshop.logo}
              layout="responsive"
              objectFit="contain"
              objectPosition="left"
              width={196}
              height={110}
              fixedHeight={true}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                "&.MuiTypography-root": {
                  fontSize: ".875rem",
                  fontWeight: basierCirleBold.style.fontWeight,
                  fontFamily: basierCirleBold.style.fontFamily,
                  textTransform: "uppercase",
                  color: theme.palette.common.black,
                },
              }}
            >
              Wann, wo & wieviel?
            </Typography>
            <Typography variant="body1">
              Am <FormattedDate value={workshop.start_date} />
              {!workshop.online ? (
                <> in {workshop.city}</>
              ) : (
                <>, online</>
              )}, {strapiTimeFrag(workshop.start_time)} bis{" "}
              {strapiTimeFrag(workshop.end_time)} Uhr
              <br />
              {workshop.price ? (
                <span>{workshop.price}€ pro Teilnehmer</span>
              ) : (
                <span>Kostenlos</span>
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              color="secondary"
              variant="outlined"
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="Mehr Infos anzeigen"
            >
              {expanded ? `Weniger Infos` : "Mehr Infos"}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{
          "&.MuiCollapse-root": {
            padding: 0,
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: grey[100],
            px: 3,
            pt: 2,
          }}
        >
          <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
            <Grid item xs={12} md={8}>
              <Grid
                container
                spacing={2}
                sx={{
                  "& .MuiGrid-root": {
                    "& .MuiTypography-root": {
                      "&:nth-of-type(odd)": {
                        fontWeight: basierCirleBold.style.fontWeight,
                        fontFamily: basierCirleBold.style.fontFamily,
                        textTransform: "uppercase",
                        fontSize: ".875rem",
                      },
                    },
                  },
                }}
              >
                {workshop.description && (
                  <Grid item xs={12}>
                    <Typography variant="body1">Worum geht&apos;s?</Typography>
                    <Typography variant="body1">
                      {workshop.description}
                    </Typography>
                  </Grid>
                )}
                {workshop.speaker && (
                  <Grid item xs={12}>
                    <Typography variant="body1">Referent</Typography>
                    <Typography variant="body1">{workshop.speaker}</Typography>
                  </Grid>
                )}
                {workshop.workshop_organizer && (
                  <Grid item xs={12}>
                    <Typography variant="body1">Veranstaltet von</Typography>
                    <Typography variant="body1">
                      {workshop.workshop_organizer.name}
                    </Typography>
                  </Grid>
                )}
                {workshop.link && (
                  <Grid item xs={12}>
                    <Box>
                      <Button
                        component={Link}
                        href={workshop.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="secondary"
                        variant="contained"
                      >
                        Weitere Infos & Anmeldung
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  );
};
